<template>
  <activity-item-wrapper :type="CommunicationType.task" :actions>
    <template #content>
      <div class="flex items-center space-x-2">
        <lf-user-selection
          v-tooltip="{
            content: $t('ACTIVITY.TASK.ASSIGNEE', {
              name: formatUserFullName(item.data.assignee),
            }),
            onShow: () => item.data.assignee,
          }"
          name="assignee"
          :value="item.data?.assignee"
          :initial-user-selection="item.data?.assignee"
          :get-paginated-options="getUsersByRole"
          :disabled="isDisabled"
          avatar-only-trigger-button
          :avatar-size="20"
          :avatar-placeholder-size="20"
          value-type="user"
          data-cy="assignee"
          @change="updateAssignee"
        />
        <task-priority-dropdown
          :priority-level="item.data.priority_level"
          class="priority-dropdown"
          data-cy="priority"
          :disabled="isDisabled"
          :show-value="false"
          custom-z-index="z-101"
          :teleport-options="{ enabled: true }"
          @change="updatePriority"
        />

        <task-due-date
          :date="item.data.due_at"
          :disabled="isDisabled"
          class="items-center task-due-date"
          :icon-calendar-size="12"
          @update="updateDueDate"
        />
        <task-status-dropdown
          :task-status="item.data.status"
          :disabled="isDisabled"
          data-cy="task-status"
          custom-z-index="z-101"
          :teleport-options="{ enabled: true }"
          @change="changeStatus"
        />
      </div>
      <activity-text
        v-if="isEditing"
        :text="item.data.body"
        :is-loading="updateTaskLoading || deleteTaskLoading"
        :placeholder="$t('ACTIVITY.TASK.TASK_DESCRIPTION')"
        @submit="updateDescription"
        @cancel="isEditing = false"
      />
      <activity-content v-else :content="item.data.body" />
      <task-footer :item="item.data" />
      <log-comments-section
        v-if="showComments"
        :comments="item.data.comments ?? []"
        :version="2"
      />
    </template>
  </activity-item-wrapper>
</template>
<script setup lang="ts">
import { computed, provide, ref } from "vue";
import { useI18n } from "vue-i18n";

import isEmpty from "lodash/isEmpty";

import { CommunicationType } from "@/enums/communicationLogs";
import { LogEndpointType } from "@/enums/communicationLogs";
import { formatUserFullName } from "@/helpers/formatting";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import { useClients } from "@/hooks/clients";
import {
  useDeleteTaskMutation,
  useUpdateTaskMutation,
} from "@/hooks/queries/useInbox";
import usersApiService from "@/services/modules/users";

import LogCommentsSection from "@/components/communicationLogs/comments/LogCommentsSection.vue";
import LogCommentsToggle from "@/components/communicationLogs/comments/LogCommentsToggle.vue";
import TaskDueDate from "@/components/communicationLogs/forms/TaskDueDate.vue";
import TaskPriorityDropdown from "@/components/communicationLogs/forms/TaskPriorityDropdown.vue";
import TaskStatusDropdown from "@/components/communicationLogs/forms/TaskStatusDropdown.vue";
import ActivityContent from "@/components/deals/dealActivity/ActivityContent.vue";
import ActivityItemWrapper from "@/components/deals/dealActivity/ActivityItemWrapper.vue";
import ActivityText from "@/components/deals/dealActivity/ActivityText.vue";
import TaskFooter from "@/components/deals/dealActivity/tasks/TaskFooter.vue";
import IconDelete from "@/components/icons/IconDelete.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import LfUserSelection from "@/components/ui/inputs/LfUserSelection.vue";

import type {
  TaskPriorityLevels,
  TaskStatuses,
} from "@/enums/communicationLogs";
import type { TaskLog } from "@/models/communicationLogs";
import type { ActivityAction } from "@/models/dealActivity";

const { item } = defineProps<{
  item: TaskLog;
}>();

provide(CommunicationLogContextKey, {
  type: LogEndpointType.Task,
  logId: item.data.id,
});

const { t } = useI18n();
const { authClientSettings: client } = useClients();

const isEditing = ref(false);
const showComments = ref(false);

const actions = computed(() => {
  const options: ActivityAction[] = [
    {
      name: "comments",
      component: LogCommentsToggle,
      props: {
        count: item.data.comments?.length ?? 0,
        modelValue: showComments.value,
      },
      tooltip: showComments.value
        ? `${t("COMMON.HIDE")} ${t("ACTIVITY.COMMENTS.TITLE", 2)}`
        : `${t("COMMON.SEE")} ${t("ACTIVITY.COMMENTS.TITLE", 2)}`,
      click: () => {
        showComments.value = !showComments.value;
      },
    },
    {
      name: "delete",
      icon: IconDelete,
      class: "text-label hover:text-error",
      click: () => {
        deleteTask(item.data.id);
      },
    },
  ];

  if (!isEditing.value) {
    options.unshift({
      name: "edit",
      icon: IconEdit,
      viewBox: "0 0 16 16",
      class: "text-label hover:text-primary",
      click: () => {
        isEditing.value = true;
      },
    });
  }

  return options;
});

const { mutateAsync: updateTaskMutation, isPending: updateTaskLoading } =
  useUpdateTaskMutation();
const { mutateAsync: deleteTaskMutation, isPending: deleteTaskLoading } =
  useDeleteTaskMutation();

const updatePriority = async (priority: TaskPriorityLevels) => {
  if (!item.data.id) {
    return;
  }
  await updateTaskMutation({
    id: item.data.id,
    priority_level: priority,
  });
};

const updateDueDate = async (due_at: string) => {
  if (!item.data.id) {
    return;
  }
  await updateTaskMutation({
    id: item.data.id,
    due_at: new Date(due_at).toISOString(),
  });
};

const deleteTask = async (id: number) => {
  if (!id) {
    return;
  }

  await deleteTaskMutation(id);
};

const updateAssignee = async (assignee_id: number) => {
  if (!item.data.id) {
    return;
  }
  await updateTaskMutation({
    id: item.data.id,
    assignee_id,
  });
};

const isDisabled = computed(
  () => updateTaskLoading.value || isEditing.value || deleteTaskLoading.value
);

const updateDescription = async (description: string) => {
  if (!item.data.id || description === item.data.body) {
    return;
  }
  await updateTaskMutation({
    id: item.data.id,
    description,
  });
  isEditing.value = false;
};

const getUsersByRole = (params: Record<string, string | number> = {}) => {
  const isAClient = !isEmpty(client.value);
  if (isAClient) {
    return usersApiService.getClientUsers(client.value.id, params);
  }
  return usersApiService.getEmployees(params);
};

const changeStatus = async (status: TaskStatuses) => {
  if (!item.data.id) {
    return;
  }
  await updateTaskMutation({ id: item.data.id, status });
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.task-due-date {
  & :deep(.selected-date) {
    @apply text-xxs;
  }

  & :deep(.date-row) {
    @apply items-center;
  }

  & :deep(.icon-container) {
    @apply flex h-4 items-center justify-center;

    & :deep(svg) {
      @apply mb-0;
    }
  }
}

.priority-dropdown {
  & :deep(.toggle-button) {
    @apply h-5;
  }
}
</style>
